import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import ChangeoverType from 'src/models/changeover-type';
import { List } from 'linqts';
import { Moment } from 'moment';
import * as moment from 'moment';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Router } from '@angular/router';
import AppRoutes from 'src/app/app-routes';
import Product from 'src/models/product';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-add',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.scss'],
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS, useValue: {
      displayDefaultIndicatorType: false,
    }
  }]
})
export class AddComponent implements OnInit, OnDestroy {
  private subscriptions: Array<Subscription> = [];
  private internalSelectedFromId: string | null = null;
  private internalSelectedToId: string | null = null;
  selectedChangeoverType: ChangeoverType | null = null;
  selectedDate: Moment | null = null;
  selectedTime: string | null = null;
  fromOptions: Array<Product> = [];
  toOptionsByFromId!: Map<string, Product[]>;
  getChangeoverTypesPromise: Promise<ChangeoverType[]> | null = null;
  scheduleChangeoverPromise: Promise<string> | null = null;
  changeoverTypes: ChangeoverType[] = [];

  constructor(
    private apiService: ApiService,
    private userSessionService: UserSessionService,
    private readonly router: Router,
  ) {
    this.subscriptions.push(
      this.userSessionService.activeLineIdChanged.subscribe(lineId => this.loadChangeoverTypes())
    );
    this.setSuggestedDateAndTime();
  }

  async ngOnInit(): Promise<void> {
    await this.loadChangeoverTypes();
  }

  async ngOnDestroy(): Promise<void> {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  getToOptions = (): Product[] =>
    this.selectedFromId
      ? this.toOptionsByFromId.get(this.selectedFromId) || []
      : [];

  getChangeoverTypeOptions = (): ChangeoverType[] => 
    this.changeoverTypes
      .filter(type => type.productChanges.some(change => change.from.id === this.selectedFromId && change.to.id === this.selectedToId));

  get formattedDate(): string | null {
    return this.selectedDate && this.selectedDate.toDate().toDateString();
  }

  private async loadChangeoverTypes() {
    this.getChangeoverTypesPromise = this.apiService.getChangeoverTypes(this.userSessionService.activeLineId);
    this.changeoverTypes = await this.getChangeoverTypesPromise;

    // Check added to bypass test-runner bug:
    if (!Array.isArray(this.changeoverTypes)) {
      return;
    }

    const productChanges = new List(this.changeoverTypes.flatMap(item => item.productChanges)).DistinctBy(item => item.id);
    this.fromOptions =
      productChanges
        .Select(item => item.from).DistinctBy(from => from.id)
        .OrderBy(from => from.name)
        .ToArray();
    this.toOptionsByFromId = new Map<string, Product[]>(this.fromOptions.map(from => [
      from.id,
      productChanges
        .Where(change => change?.from.id === from.id)
        .Select(change => change.to)
        .DistinctBy(item => item.id)
        .OrderBy(to => to.name)
        .ToArray()
    ]));

    const defaultProductChange = productChanges.OrderBy(item => item.from.name).ThenBy(item => item.to.name).FirstOrDefault();
    this.selectedFromId = defaultProductChange?.from.id;
    this.selectedToId = this.getToOptions()[0]?.id || null;
    this.selectedChangeoverType = this.getChangeoverTypeOptions()[0] || null;

    this.getChangeoverTypesPromise = null;
  }

  scheduleChangeover = async () => {
    if (!this.selectedDate || !this.selectedTime || !this.selectedFromId || !this.selectedToId || !this.selectedChangeoverType) {
      return;
    }
    const hour = parseInt(this.selectedTime.split(':')[0]);
    const minute = parseInt(this.selectedTime.split(':')[1]);
    const scheduledTime = moment(this.selectedDate).hour(hour).minute(minute).toISOString();

    this.scheduleChangeoverPromise =
      this.apiService.scheduleChangeover(
        this.selectedChangeoverType.id,
        this.selectedFromId,
        this.selectedToId,
        scheduledTime);

    await this.scheduleChangeoverPromise;
    this.scheduleChangeoverPromise = null;
    this.router.navigate([AppRoutes.LineSchedule]);
  }

  private setSuggestedDateAndTime() {
    this.selectedDate = moment().startOf('d').add(1, 'd');
    const now = moment();
    const slotSize = 15;
    const nextSlot = (now.minutes() - (now.minutes() % slotSize)) + (2 * slotSize);
    this.selectedTime = now.startOf('h').add(nextSlot, 'minutes').format('HH:mm');
  }

  get selectedFromId(): string | null { return this.internalSelectedFromId; }
  set selectedFromId(value: string | null) {
    this.internalSelectedFromId = value;
    const toOptions = this.getToOptions();
    this.selectedToId = toOptions.length === 1 ? toOptions[0].id : null;
  }
  get selectedToId(): string | null { return this.internalSelectedToId; }
  set selectedToId(value: string | null) {
    this.internalSelectedToId = value;
    const typeOptions = this.getChangeoverTypeOptions();
    this.selectedChangeoverType = typeOptions.length === 1 ? typeOptions[0] : null;
  }
}
